import React, {useCallback, useContext, useEffect, useState} from 'react';
import {
  LineChart,
  XAxis,
  Legend,
  ResponsiveContainer,
  Line,
  ReferenceLine,
  Label,
  Tooltip,
  YAxis,
} from 'recharts';
import CustomLegend from './CustomLegend';
import {DataType, VehiclesType, YearType} from '../../types/data';
import {VehicleContext} from '../../context/modules/VehicleContext';
import CustomTooltip from './CustomTooltip';
import useWindowSize from '../../hooks/use-window-size';
import {styled} from '@volkswagen-onehub/components-core';
import CustomReferenceLabel from './CustomReferenceLabel';
import { formatCurrency } from '../../app.utils';

type MarginType = {top: number; right: number; left: number; bottom: number};

type HeightType = string | number;

type MarginHeightType = {
  height: HeightType;
  margin: MarginType;
};

type MarginHeightTypes = {
  mb: MarginHeightType;
  dt: MarginHeightType;
};

type LegendPosition = {
  left: number;
  top: number;
};

const marginHeight: MarginHeightTypes = {
  mb: {
    height: 500,
    margin: {
      top: 160,
      right: 100,
      left: 60,
      bottom: 0,
    },
  },
  dt: {
    height: 500,
    margin: {
      top: 20,
      right: 100,
      left: 100,
      bottom: 0,
    },
  },
};

const ChartContainer = styled.div`
  width: 100%;
  overflow: hidden;
  
  .recharts-layer.recharts-cartesian-axis.recharts-yAxis.yAxis
    > g
    > g:nth-child(1)
    > text
    > tspan {
    display: none;
  }
`;

const labelsen = require('./../../data/en.json');
const labelsfr = require('./../../data/fr.json');

interface ChartLocaleService {
  localeService: any;
}

export default (props: ChartLocaleService) => {
  const [data, setData] = useState<DataType[] | null>(null);
  const [label, setLabel] = useState(labelsen);
  const vehicleCtx = useContext(VehicleContext);

  const [margin, setMargin] = useState<MarginHeightType>(marginHeight.mb);
  const [legendPosition, setLegendPosition] = useState<LegendPosition>({
    top: 0,
    left: 70,
  });

  const [width] = useWindowSize();

  const renderLines = () => {
    const vehicles = vehicleCtx?.value;

    return vehicles ? (
      <React.Fragment>
        {vehicles.map((v, index) => {
          const isVw: boolean = v.Make == 'Volkswagen';
          const stroke: string = isVw ? '#00437A' : '#DFE4E8';

          return (
            <Line
              activeDot={{fill: stroke, stroke: '#fff'}}
              dot={{fill: '#fff', stroke: stroke, strokeWidth: 2}}
              key={v.Model}
              type="monotone"
              dataKey={'year' + (index + 1)}
              name={JSON.stringify({
                year: v.Year,
                make: v.Make,
                model: v.Model,
                series: v.Series,
                total: v.total,
              })}
              stroke={stroke}
              strokeWidth={v.Make == 'Volkswagen' ? 4 : 2}
            />
          );
        })}
      </React.Fragment>
    ) : null;
  };

  const renderReferenceLines = () => {
    const totalValues = data?.length ? data[data.length - 1] : null;

    const vw = vehicleCtx?.value?.filter((v) => v.Make == 'Volkswagen');
    let vwTotal: number = 0;

    if (vw) {
      vwTotal =
        Number(vw[0].year1) +
        Number(vw[0].year2) +
        Number(vw[0].year3) +
        Number(vw[0].year4) +
        Number(vw[0].year5);
    }

    return (
      totalValues && (
        <React.Fragment>
          {Object.keys(totalValues).map((k) => {
            const isVw: boolean =
              Number(totalValues[k as keyof DataType]) === vwTotal;
            return isVw ? (
              <ReferenceLine
                position="middle"
                stroke="undefined"
                key={k}
                segment={[
                  {x: label.year_5, y: Number(totalValues[k as keyof DataType])},
                  {
                    x: label.year_5,
                    y: Number(totalValues[k as keyof DataType]) + 5,
                  },
                ]}
              >
                <Label
                  value={formatCurrency(totalValues[k as keyof DataType] as number, props.localeService.language)}
                  position="right"
                  style={{fontWeight: isVw ? 'bold' : 'undefined'}}
                  fill={isVw ? '#00437A' : '#ccc'}
                  content={CustomReferenceLabel}
                />
              </ReferenceLine>
            ) : null;
          })}
        </React.Fragment>
      )
    );
  };

  useEffect(() => {
    const vehicles = vehicleCtx?.value;

    if (vehicles) {
      const year3Obj: DataType = {
        legend: label.year_3,
      };

      const year4Obj: DataType = {
        legend: label.year_4,
      };

      const year5Obj: DataType = {
        legend: label.year_5,
      };

      let dataValues = [];

      for (let index = 0; index < vehicles.length; index++) {
        const detail = vehicles[index];
        // year1Obj['year' + (index + 1) as YearType] = Number(detail.year1);
        // year2Obj['year' + (index + 1) as YearType] = Number(detail.year1) + Number(detail.year2);
        year3Obj[('year' + (index + 1)) as YearType] =
          Number(detail.year1) + Number(detail.year2) + Number(detail.year3);
        year4Obj[('year' + (index + 1)) as YearType] =
          Number(detail.year1) +
          Number(detail.year2) +
          Number(detail.year3) +
          Number(detail.year4);
        year5Obj[('year' + (index + 1)) as YearType] =
          Number(detail.year1) +
          Number(detail.year2) +
          Number(detail.year3) +
          Number(detail.year4) +
          Number(detail.year5);
      }

      dataValues.push(year3Obj, year4Obj, year5Obj);

      setData(dataValues);
    }
  }, [vehicleCtx?.value]);

  useEffect(() => {
    if (!vehicleCtx?.value) return;

    if (width > 980) {
      setMargin(marginHeight.dt);
    } else {
      const curMarginHeightMb = marginHeight.mb;
      curMarginHeightMb.margin.top =
        vehicleCtx?.value.length * 25 <= 200
          ? vehicleCtx?.value.length * 25
          : 200;
      setMargin(curMarginHeightMb);
    }

    if (width > 480) {
      setLegendPosition({top: 0, left: 180});
    } else {
      setLegendPosition({top: 0, left: 70});
    }
  }, [width, vehicleCtx?.value]);

  useEffect(() => {
    if(props.localeService.language == 'fr') {
      setLabel(labelsfr);
    }
  }, []);

  const getVWName = useCallback(() => {
      if (vehicleCtx?.value) {
        const modelData = vehicleCtx.value.find(x => x.Make === "Volkswagen") as VehiclesType;
        const vehicle = props.localeService.language === "fr" ? `${modelData.Make} ${modelData.Model} ${modelData.Series} ${modelData.Year}` : `${modelData.Year} ${modelData.Make} ${modelData.Model} ${modelData.Series}`;
        return label.graph_title.replace("%%vehicle%%", vehicle);
      } else {
        return "";
      }
  }, [label, vehicleCtx]);

  return data && vehicleCtx?.value ? (
    <ChartContainer>
      <ResponsiveContainer
        id="cost-compare-chart"
        height={
          (margin.height as number) +
          (vehicleCtx?.value.length > 5 ? vehicleCtx?.value.length * 15 : 0)
        }
        width="100%"
      >
        <LineChart
          title={getVWName()}
          height={300}
          width={100}
          data={data as any[]}
          margin={margin.margin}
          accessibilityLayer
          role="application"
          aria-label={`${getVWName()}. ${label.voiceover_note}`}
        >
          <XAxis
            dataKey="legend"
            height={100}
            tickLine={false}
            style={{fontWeight: 'bold'}}
            fill="#000"
          />

          <YAxis
            orientation="left"
            tickLine={false}
            tickFormatter={(value) => formatCurrency(value, props.localeService.language)}
            domain={['auto', 'auto']}
            tickCount={5}
            allowDataOverflow={true}
            interval={0}
          />

          <Legend
            verticalAlign="top"
            height={36}
            align="left"
            layout="vertical"
            iconType="circle"
            wrapperStyle={legendPosition}
            content={<CustomLegend />}
          />

          {renderLines()}

          {renderReferenceLines()}

          <Tooltip content={<CustomTooltip language={props.localeService.language} />} cursor={false} />
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  ) : null;
};
